<script setup>
const route = useRoute();
const nuxtApp = useNuxtApp();

const { getMenu } = useWpMenu();
const props = defineProps({
  type: String,
  theme: {
    type: String,
    default: "indigo",
  },
  alternateActivePath: [String, null],
});

const { data: secondaryMenuData } = await getMenu("secondary", nuxtApp);

const applicableSecondaryTopLevel = computed(() =>
  secondaryMenuData.value[0]?.find(
    (group) => group.title.toLowerCase() === props.type.toLowerCase()
  )
);

const menuItems = computed(() => {
  if (applicableSecondaryTopLevel.value?.children.length > 0) {
    // insert "overview" link
    const overview = {
      id: "media-overview",
      title: "Overview",
      url: `/${props.type}/`,
      status: "publish",
      object_status: "publish",
      order: 0,
    };
    const publishedItems = applicableSecondaryTopLevel.value?.children
      .filter((item) => item.object_status === "publish")
      .sort((a, b) => a - b);
    return [overview, ...publishedItems];
  }
  return [];
});

const openOverlay = () => {
  useEvent("overlay:open", {
    data: {},
    type: "newsletter",
  });
};
const emit = defineEmits(["subnav-click"]);

const scrollToBottomHero = () => {
  emit("subnav-click", true);
};
</script>

<template>
  <nav
    :data-type="type"
    class="sub-navigation"
    :class="{
      '-white': theme === 'white',
      '-loud-indigo': theme === 'loud-indigo',
      '-grey': theme === 'grey',
    }"
    ref="subNavigation"
  >
    <div class="sub-navigation__inner page-margin">
      <ul
        class="menu"
        v-if="!$viewport.isLessThan('desktopView') && menuItems?.length"
      >
        <li v-for="item in menuItems" :key="item.id" class="menu-item">
          <WSALink
            v-if="!alternateActivePath"
            :to="item.url"
            :exactActiveClass="'-active'"
            class="menu-link"
            @click="scrollToBottomHero"
          >
            {{ item.title }}
          </WSALink>
          <WSALink
            v-if="alternateActivePath"
            :to="item.url"
            :class="{ '-active': item.url === alternateActivePath }"
            class="menu-link"
            @click="scrollToBottomHero"
          >
            {{ item.title }}
          </WSALink>
        </li>
      </ul>
      <ElementsMenuDropdown
        v-if="$viewport.isLessThan('desktopView') && menuItems?.length"
        :items="menuItems"
        :alternateActivePath="alternateActivePath"
        :theme="theme"
        @subnav-mobile-click="scrollToBottomHero"
      />
      <div class="newsletter-wrapper" v-if="constants.ENABLE_NEWSLETTER">
        <ButtonCTA
          :to="'#'"
          label="Stay updated"
          class="cta"
          type="fourth"
          :hasIcon="true"
          icon="arrowsimple"
          @click="openOverlay"
        />
      </div>
    </div>
  </nav>
</template>

<style lang="postcss" scoped>
.sub-navigation {
  --bg: var(--deep-indigo);
  --text: var(--white);
  --text-hover: var(--white);
  --text-active: var(--white);
  --line-scale: 0;
  --line-origin: bottom right;

  position: sticky;
  top: var(--header-height);
  z-index: 98;

  padding-block: units(1.5);
  background-color: var(--bg);
  color: var(--text);

  &.-white {
    --bg: var(--white);
    --text: var(--black);
    --text-active: var(--loud-indigo);
    --text-hover: var(--loud-indigo);
  }
  &.-loud-indigo {
    --bg: var(--loud-indigo);
    --text: var(--white);
    --text-active: var(--white);
    --text-hover: var(--white);
  }
  &.-grey {
    --bg: var(--grey-0);
    --text: var(--grey-4);
    --text-active: var(--loud-indigo);
    --text-hover: var(--loud-indigo);
  }

  &__inner {
    display: grid;
    grid-template-columns: 1fr;

    @media (--md) {
      grid-template-columns: 1fr max-content;
    }
  }
}

.newsletter-wrapper {
  display: none;
  padding: 0;
  padding-right: 0;

  @media (--md) {
    display: block;
  }

  .cta {
    padding: 0;
    color: var(--text);
    opacity: 0.8;

    :deep(.icon) {
      width: 0.8em;
    }

    @media (--md) {
      padding: units(0.8) units(1.2);
      padding-right: 0;
    }
  }
}

ul,
li {
  margin: 0;
  padding: 0;
  list-style: none;
}

.menu {
  &-item {
    position: relative;
    display: inline-block;

    &:first-child .menu-link {
      padding-inline-start: 0;

      &:after {
        left: 0;
        width: calc(100% - units(1.5));
      }
    }
    &:last-child .menu-link {
      padding-inline-end: 0;

      &:after {
        width: calc(100% - units(1.5));
      }
    }
  }

  &-link {
    position: relative;
    display: block;
    color: var(--text);
    text-decoration: none;
    padding: units(0.8) units(1.5);

    &.-active {
      color: var(--text-active);
    }
    &:hover {
      color: var(--text-hover);
    }

    &:after {
      content: "";
      position: absolute;
      left: units(1.5);
      bottom: 2px;
      width: calc(100% - (units(1.5) * 2));
      height: 1px;
      transform: scaleX(var(--line-scale));
      transform-origin: var(--line-origin);
      background-color: currentColor;
      opacity: 1;
      transition: opacity 0.35s ease, transform 0.35s ease;
    }

    &.-active:after {
      opacity: 0.5;
      --line-scale: 1;
    }
    &.-active:hover:after {
      opacity: 1;
    }
    /* &:hover:after {
      opacity: 0.5;
    } */

    /* &:after {
      content: "";
      position: absolute;
      width: 100%;
      transform: scaleX(var(--line-scale));
      height: 1px;
      bottom: 0;
      left: 0;
      background-color: currentColor;
      transform-origin: var(--line-origin);
      transition: transform 0.3s ease-out;
    } */

    &:hover {
      --line-scale: 1;
      --line-origin: bottom left;
    }
  }
}
</style>
